import React, { useState, useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

// Custom.
import Wrapper from "../Content/Wrapper";
import Report from "../Content/Report";
import Row from "../Content/Row";
import I18n from "@vocollege/app/dist/modules/Services/I18n";
import { GET_STATISTICS_COURSE_MOMENTS } from "api";
import { GeneralObject } from "@vocollege/app/dist/global";
import Header from "../Content/Header";
import VoLoader from "@vocollege/components/dist/VoLoader";
import { getUserString } from "@vocollege/app/dist/modules/VoHelpers";
import { getCourseMomentsParticipants } from "./Utils";
import { DownloadExcelButtonProps } from "@vocollege/components/dist/DownloadExcelButton";
import { getExcelData } from "./Utils";

const CourseMoments: React.FC = () => {
  const [rawData, setRawData] = useState<GeneralObject[]>([]);
  const [excelData, setExcelData] = useState<DownloadExcelButtonProps | null>(
    null
  );

  // Methods.

  const handleHeaderChange = (params: GeneralObject) => {
    loadQuery({
      variables: {
        from: params.from,
        to: params.to,
        groupBy: params.groupBy,
      },
    });
  };

  // Api.

  const [loadQuery, { data, loading, variables }] = useLazyQuery(
    GET_STATISTICS_COURSE_MOMENTS,
    {
      fetchPolicy: "network-only",
      onCompleted: (loadedData) => {
        setRawData(JSON.parse(loadedData?.certStatisticsCourseMoments?.data));
      },
    }
  );

  // Effects.

  useEffect(() => {
    setExcelData(getExcelData(data, rawData, variables));
  }, [rawData, data, variables]);

  return (
    <Wrapper
      title={I18n.get.vocertCourseMoment.labels.courseMoments}
      hideTitleOnPrint
    >
      <Header
        filters={["from", "to"]}
        onChange={handleHeaderChange}
        excel={excelData || undefined}
      />

      {!loading && rawData.length > 0 && (
        <Box
          sx={{
            mt: 4,
            "@media print": {
              mt: 0,
            },
          }}
        >
          <Report
            title={I18n.get.vocertCourseMoment.labels.courseMoments}
            date={data?.certStatisticsCourseMoments?.createdAt}
            interval={`${variables?.from} - ${variables?.to}`}
            summary={
              <Box>
                <Row
                  label={I18n.get.vocertStatistics.total}
                  value={rawData.length}
                />
                <Row
                  label={I18n.get.participant.labels.participants}
                  value={getCourseMomentsParticipants(rawData)}
                />
              </Box>
            }
          >
            <Stack>
              {rawData.map((v: GeneralObject, i: number) => (
                <Box key={i} sx={{ mb: 2, "&:last-child": { mb: 0 } }}>
                  <Row
                    key={i}
                    label={v.title}
                    value={
                      <Stack alignItems="flex-end">
                        {v.author_email && (
                          <Typography variant="caption">
                            {getUserString({
                              firstname: v.author_firstname,
                              lastname: v.author_lastname,
                              email: v.author_email,
                            })}
                          </Typography>
                        )}
                        <Typography variant="caption">
                          {`${I18n.get.vocertMisc.when}: ${v.start_date} - ${v.end_date}`}
                        </Typography>
                        <Typography variant="caption">
                          {`${I18n.get.group.categories.local}: ${
                            v.college_title || I18n.get.misc.notVoCollege
                          }`}
                        </Typography>
                        <Typography variant="caption">
                          {`${I18n.get.participant.labels.participants}: ${v.participants_count}`}
                        </Typography>
                        <Typography variant="caption">
                          {`${I18n.get.form.labels.items}: ${v.course_moment_items_count}`}
                        </Typography>
                      </Stack>
                    }
                  />
                </Box>
              ))}
            </Stack>
          </Report>
        </Box>
      )}
      {loading && <VoLoader />}
    </Wrapper>
  );
};

export default CourseMoments;
